import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { photoCrud } from "@/api/photo/actions";
import { Button, Icon } from "@/components/common";
import { ModalBase } from "@/components/common/modals";
import { useInfiniteFetch } from "@/hooks/common/use-infinite-fetch";
import { isValidImage, toast } from "@/utils";

export interface ImportedPhotoData {
  url: string;
  isNew: boolean;
  photoId?: string;
  file?: File;
}

interface PhotoImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: ImportedPhotoData) => void;
}

type TabType = "book" | "upload";

export const PhotoImportModal = ({ isOpen, onClose, onImport }: PhotoImportModalProps) => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>("book");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedBookPhoto, setSelectedBookPhoto] = useState<{ url: string; id: string } | null>(null);

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTimeRef = useRef<number>(0);

  const currentSelectedPhoto = activeTab === "book" ? selectedBookPhoto : uploadedPhoto;

  const {
    data: response,
    isLoading: isLoadingApi,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteFetch((pageParam) => photoCrud.getBookPhotos(id as string, pageParam, 9), {
    queryKey: ["talent-photos", "infinite", id],
    includeDepartmentInCache: true,
    enabled: isOpen && activeTab === "book" && !!id,
  });

  const bookPhotos = useMemo(() => {
    if (!response?.pages) {
      return [];
    }

    return response.pages.flatMap((page) => page.data).filter(Boolean);
  }, [response?.pages]);

  useEffect(
    function revokePreviousBlobUrl() {
      return () => {
        if (uploadedPhoto?.startsWith("blob:")) {
          URL.revokeObjectURL(uploadedPhoto);
        }
      };
    },
    [uploadedPhoto],
  );

  const handleRemoveUploaded = () => {
    if (uploadedPhoto?.startsWith("blob:")) {
      URL.revokeObjectURL(uploadedPhoto);
    }
    setUploadedPhoto(null);
    setUploadedFile(null);
  };

  useEffect(
    function resetModalStateOnOpen() {
      if (isOpen) {
        setActiveTab("book");
        setSelectedBookPhoto(null);
        handleRemoveUploaded();
      }
    },
    [isOpen],
  );

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage || isLoadingApi) {
      return;
    }
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 200) {
      return;
    }
    lastScrollTimeRef.current = now;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoadingApi, fetchNextPage]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true);
    const file = event.target.files?.[0];
    if (!file) {
      setIsUploading(false);
      return;
    }

    if (!isValidImage(file)) {
      setIsUploading(false);
      event.target.value = "";
      return;
    }

    try {
      if (uploadedPhoto?.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedPhoto);
      }
      const previewUrl = URL.createObjectURL(file);
      setUploadedPhoto(previewUrl);
      setUploadedFile(file);
    } catch (_error) {
      toast.error("Erro ao processar imagem.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportClick = () => {
    if (activeTab === "book" && selectedBookPhoto) {
      onImport({
        url: selectedBookPhoto.url,
        photoId: selectedBookPhoto.id,
        isNew: false,
      });
    } else if (activeTab === "upload" && uploadedPhoto && uploadedFile) {
      onImport({
        url: uploadedPhoto,
        file: uploadedFile,
        isNew: true,
      });
    }
    onClose();
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      hasCloseButton={false}
      className={clsx(
        "flex flex-col bg-background-primary p-6 sm:p-8 transition-all duration-300",
        activeTab === "book" ? "w-full max-w-4xl" : "w-full max-w-xl",
      )}
    >
      <div className="flex flex-col items-center text-center mb-6">
        <h3>Importe uma foto</h3>
        <p className="details-text mt-2">Selecione uma foto do book ou do dispositivo.</p>
      </div>

      <div className="flex w-full border-b border-input-border mb-6">
        <button
          onClick={() => setActiveTab("book")}
          className={clsx(
            "flex-1 pb-3 text-sm border-b-2",
            activeTab === "book" ? "border-primary text-primary" : "border-transparent text-label",
          )}
        >
          Fotos do book
        </button>
        <button
          onClick={() => setActiveTab("upload")}
          className={clsx(
            "flex-1 pb-3 text-sm border-b-2",
            activeTab === "upload" ? "border-primary text-primary" : "border-transparent text-label",
          )}
        >
          Upload de foto
        </button>
      </div>

      {activeTab === "book" && (
        <div className="flex flex-col h-[60vh] min-h-[450px] overflow-hidden">
          {isLoadingApi && bookPhotos.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Icon name="Sync" className="animate-spin" />
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="h-full overflow-y-auto pr-2 pb-2 mini-scrollbar"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {bookPhotos.map((photo: any, index) => {
                  const isSelected = selectedBookPhoto?.id === photo.id;
                  return (
                    <div
                      key={`${photo.id}-${index}`}
                      onClick={() => setSelectedBookPhoto({ url: photo.url, id: photo.id })}
                      className={clsx(
                        "relative aspect-[3/4] cursor-pointer border-2 transition-all",
                        isSelected ? "border-primary shadow-md" : "border-transparent hover:border-gray",
                      )}
                    >
                      <img src={photo.url} alt="Book" className="w-full h-full object-cover" />
                      {isSelected && (
                        <div className="absolute top-2 left-2 w-8 h-8 bg-white flex items-center justify-center shadow-sm">
                          <div className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center">
                            <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {isFetchingNextPage && (
                <div className="py-4 text-center text-primary">
                  <Icon name="Sync" className="animate-spin" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "upload" && (
        <div className="flex flex-col items-center justify-center py-10 min-h-[160px]">
          {uploadedPhoto ? (
            <div className="relative w-48 aspect-[3/4] border">
              <img src={uploadedPhoto} className="w-full h-full object-cover" />
              <button
                onClick={handleRemoveUploaded}
                className="absolute top-2 right-2 p-2 bg-white rounded-md text-error shadow-sm"
              >
                <Icon name="Trash" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} accept="image/*" />
              <Button
                variant="secondary"
                className="h-12"
                isLoading={isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                Selecionar arquivo
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="shrink-0 mt-6 flex justify-between gap-4 pt-6 border-t border-input-border">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleImportClick} disabled={!currentSelectedPhoto || isUploading}>
          Importar foto
        </Button>
      </div>
    </ModalBase>
  );
};
